import { useEffect, useState } from 'react';
import { Crown, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * WBTRecordCard — Settings-page card that shows the CURRENT World Best Time
 * record the rower is chasing, based on their profile (sex, weight_class,
 * para_classification) × the boat class currently selected on the splash
 * screen. Only ever renders one card at a time.
 *
 * Props:
 *   boatClass — the boat slug the rower has picked (e.g. '1x', 'c2_erg')
 *   isErgo    — true when the current app mode is Mode 3 (Ergo)
 */
export default function WBTRecordCard({ boatClass, isErgo = false }) {
  const [record, setRecord] = useState(null);
  const [mode, setMode] = useState('on_water');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ mode: isErgo ? 'ergo' : 'on_water' });
        if (boatClass && !isErgo) qs.set('boat_class', boatClass);
        const res = await fetch(`${API}/wbt/lookup?${qs.toString()}`, { credentials: 'include' });
        if (!res.ok) { if (!cancelled) { setRecord(null); setLoading(false); } return; }
        const data = await res.json();
        if (!cancelled) {
          setRecord(data.record || null);
          setMode(data.mode || (isErgo ? 'ergo' : 'on_water'));
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setRecord(null); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [boatClass, isErgo]);

  if (loading) return null;

  // No matching record — friendly placeholder (not the silent-crown state).
  if (!record) {
    return (
      <div
        className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4"
        data-testid="wbt-record-card-empty"
      >
        <div className="flex items-center gap-3 mb-2">
          <Crown className="w-5 h-5 text-zinc-400" />
          <h3 className="font-bold text-zinc-500 dark:text-zinc-400 text-sm uppercase tracking-widest">
            World Best Time
          </h3>
        </div>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No record on file for this combination yet.
        </p>
        <p className="text-xs text-zinc-400 mt-1">
          Make sure your profile has a sex, weight class, and (if applicable) para classification set — the lookup uses those plus your boat pick.
        </p>
      </div>
    );
  }

  const isErgoRec = mode === 'ergo';

  // ── Ergo record layout — 11 spreadsheet columns ──────────────────────────
  if (isErgoRec) {
    return (
      <div
        className="bg-white dark:bg-zinc-800 border-2 border-amber-200 dark:border-amber-900/40 rounded-xl p-4"
        data-testid="wbt-record-card"
      >
        <div className="flex items-center gap-3 mb-3">
          <Trophy className="w-5 h-5 text-amber-500" fill="#F4C430" />
          <h3 className="font-bold text-zinc-800 dark:text-zinc-100 text-sm uppercase tracking-widest">
            World Best Time · Ergo
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <KV label="Athlete" value={record.athlete} testid="wbt-athlete" />
          <KV label="Nationality" value={record.nationality} testid="wbt-nation" />
          <KV label="Gender" value={record.sex === 'male' ? 'Men' : 'Women'} testid="wbt-gender" />
          <KV label="Weight Class" value={record.weight_class === 'lightweight' ? 'Lightweight' : 'Open (Heavyweight)'} testid="wbt-wc" />
          <KV label="Age Category" value={record.age_category} testid="wbt-age" />
          <KV label="Machine" value={record.machine_type} testid="wbt-machine" />
          <KV label="Distance" value={`${record.distance_m} m`} testid="wbt-distance" />
          <KV label="Record Time" value={record.record_time} testid="wbt-time" mono />
          <KV label="Total Time" value={`${record.total_time_s} s`} testid="wbt-total" mono />
          <KV label="Split /500m" value={record.split_500m} testid="wbt-split" mono />
          <KV label="Date Set" value={record.date_set} testid="wbt-date" />
        </div>
      </div>
    );
  }

  // ── On-water record layout — all 12 spreadsheet columns ──────────────────
  return (
    <div
      className="bg-white dark:bg-zinc-800 border-2 border-amber-200 dark:border-amber-900/40 rounded-xl p-4"
      data-testid="wbt-record-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <Trophy className="w-5 h-5 text-amber-500" fill="#F4C430" />
        <h3 className="font-bold text-zinc-800 dark:text-zinc-100 text-sm uppercase tracking-widest">
          World Best Time · {record.boat_class}
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <KV label="Crew" value={record.crew} testid="wbt-crew" wide />
        <KV label="Nation" value={record.nation} testid="wbt-nation" />
        <KV label="Time" value={record.time} testid="wbt-time" mono />
        <KV label="Total (s)" value={record.time_seconds} testid="wbt-secs" mono />
        <KV label="Split /500m" value={record.split_500m} testid="wbt-split" mono />
        <KV label="Speed" value={`${record.avg_speed_mps} m/s`} testid="wbt-mps" mono />
        <KV label="Speed" value={`${record.avg_speed_kmh} km/h`} testid="wbt-kmh" mono />
        <KV label="Date" value={record.date} testid="wbt-date" />
        <KV label="Location" value={record.location} testid="wbt-location" />
        <KV label="Event" value={record.label} testid="wbt-event" wide />
      </div>
    </div>
  );
}

function KV({ label, value, testid, mono = false, wide = false }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{label}</div>
      <div
        className="font-bold text-zinc-800 dark:text-zinc-100"
        style={mono ? { fontFamily: 'JetBrains Mono, monospace' } : undefined}
        data-testid={testid}
      >
        {value || '—'}
      </div>
    </div>
  );
}
